import aimedicLogoUrl from 'img/aimedic.svg';
import type { PlaqueVesselData, ReportLabels, VesselData } from './PlaqueReportModalContent';

export interface PlaquePdfPatientInfo {
  scanDate?: string | null;
  patientId?: string | null;
  patientName?: string | null;
  patientSex?: string | null;
  patientBirthdate?: string | null;
  patientAge?: string | null;
}

export interface PlaquePdfExportInput {
  patientInfo?: PlaquePdfPatientInfo;
  reportData: PlaqueVesselData[];
  threeDImage?: string;
  agatstonScore?: number[];
}

type NumericValue = number | null;

type PdfImagePage = {
  data: Uint8Array;
  width: number;
  height: number;
};

const PAGE_WIDTH = 1240;
const PAGE_HEIGHT = 1754;
const PDF_PAGE_WIDTH = 595.28;
const PDF_PAGE_HEIGHT = 841.89;

const COLOR = {
  red: '#b51f35',
  redText: '#b8293b',
  dark: '#252525',
  gray: '#555555',
  lightGray: '#e5e5e5',
  tableHeader: '#172742',
  totalBg: '#fff2f2',
  black: '#111111',
  white: '#ffffff',
};

const vesselColumns = ['LM', 'LAD', 'LCX', 'RCA', 'Total'] as const;
type VesselColumn = (typeof vesselColumns)[number];

const plaquePdfRows: Array<{
  label: string;
  source: ReportLabels;
}> = [
  { label: 'Length (mm)', source: 'Length (mm)' },
  { label: 'Plaque Volume (mm\u00b3)', source: 'Plaque Volume (mm\u00b3)' },
  { label: 'Calcified Plaque (mm\u00b3)', source: 'Calcified Plaque Volume (mm\u00b3, %)' },
  { label: 'Noncalcified Plaque (mm\u00b3)', source: 'Noncalcified Plaque Volume (mm\u00b3, %)' },
  { label: 'Low Attenuation Plaque (mm\u00b3)', source: 'Low Attenuation Plaque Volume (mm\u00b3, %)' },
];

function createPageCanvas() {
  const canvas = document.createElement('canvas');
  canvas.width = PAGE_WIDTH;
  canvas.height = PAGE_HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context is not available.');
  ctx.fillStyle = COLOR.white;
  ctx.fillRect(0, 0, PAGE_WIDTH, PAGE_HEIGHT);
  ctx.textBaseline = 'alphabetic';
  return { canvas, ctx };
}

function setFont(
  ctx: CanvasRenderingContext2D,
  size: number,
  weight: 'normal' | 'bold' | 'italic' | 'bold italic' = 'normal',
) {
  ctx.font = `${weight} ${size}px Arial, Helvetica, sans-serif`;
}


function addRoundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
}

function drawLine(ctx: CanvasRenderingContext2D, x1: number, y: number, x2: number) {
  ctx.strokeStyle = COLOR.red;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(x1, y);
  ctx.lineTo(x2, y);
  ctx.stroke();
}

function drawFallbackAimedicLogo(ctx: CanvasRenderingContext2D, x: number, y: number) {
  ctx.save();
  ctx.fillStyle = '#e5394a';
  const s = 15;
  const gap = 5;
  const blocks = [
    [0, 0], [1, 0], [0, 1], [1, 1],
    [3, 0], [4, 0], [3, 1], [4, 1],
  ];
  blocks.forEach(([cx, cy]) => {
    ctx.beginPath();
    addRoundRectPath(ctx, x + cx * (s + gap), y + cy * (s + gap), s, s, 3);
    ctx.fill();
  });
  setFont(ctx, 39, 'bold');
  ctx.fillStyle = '#777777';
  ctx.fillText('AiMEDiC', x + 92, y + 43);
  ctx.fillStyle = '#e5394a';
  ctx.beginPath();
  ctx.arc(x + 207, y + 7, 5, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawAimedicLogo(
  ctx: CanvasRenderingContext2D,
  logo: HTMLImageElement | null,
  x: number,
  y: number,
  maxWidth: number,
  maxHeight: number,
) {
  if (!logo) {
    drawFallbackAimedicLogo(ctx, x, y);
    return;
  }

  const sourceWidth = logo.naturalWidth || logo.width;
  const sourceHeight = logo.naturalHeight || logo.height;
  if (!sourceWidth || !sourceHeight) {
    drawFallbackAimedicLogo(ctx, x, y);
    return;
  }

  const scale = Math.min(maxWidth / sourceWidth, maxHeight / sourceHeight);
  const width = sourceWidth * scale;
  const height = sourceHeight * scale;
  ctx.drawImage(logo, x, y + (maxHeight - height) / 2, width, height);
}

function drawSectionHeader(
  ctx: CanvasRenderingContext2D,
  title: string,
  x: number,
  y: number,
  width: number,
) {
  ctx.fillStyle = COLOR.red;
  ctx.fillRect(x, y - 46, 12, 68);
  setFont(ctx, 32, 'bold');
  ctx.fillStyle = COLOR.dark;
  ctx.fillText(title, x + 52, y - 12);
  drawLine(ctx, x + 30, y + 18, x + width, y + 18);
}

function drawWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  fontSize: number,
  color = COLOR.gray,
  weight: 'normal' | 'bold' | 'italic' | 'bold italic' = 'normal',
) {
  setFont(ctx, fontSize, weight);
  ctx.fillStyle = color;
  const words = text.split(/\s+/).filter(Boolean);
  let line = '';
  let currentY = y;
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, currentY);
      line = word;
      currentY += lineHeight;
    } else {
      line = test;
    }
  }
  if (line) {
    ctx.fillText(line, x, currentY);
    currentY += lineHeight;
  }
  return currentY;
}

function drawMultiline(
  ctx: CanvasRenderingContext2D,
  lines: string[],
  x: number,
  y: number,
  width: number,
  lineHeight: number,
  size: number,
  color = COLOR.gray,
  weight: 'normal' | 'bold' | 'italic' | 'bold italic' = 'normal',
) {
  let currentY = y;
  lines.forEach((line) => {
    currentY = drawWrappedText(ctx, line, x, currentY, width, lineHeight, size, color, weight);
  });
  return currentY;
}

function normalizeDate(value?: string | null) {
  if (!value) return '-';
  const raw = String(value).trim();
  const compact = raw.replace(/[^0-9]/g, '');
  if (compact.length >= 8) {
    return `${compact.slice(0, 4)}.${compact.slice(4, 6)}.${compact.slice(6, 8)}`;
  }
  return raw.replace(/-/g, '.');
}

function normalizeSex(value?: string | null) {
  const v = (value ?? '').trim().toUpperCase();
  if (v === 'M' || v === 'MALE') return 'Male';
  if (v === 'F' || v === 'FEMALE') return 'Female';
  if (v === 'O' || v === 'OTHER') return 'Other';
  return value || '-';
}

function normalizeAge(value?: string | null) {
  if (!value) return '-';
  const match = String(value).match(/(\d+)/);
  if (!match) return String(value);
  return `${Number(match[1])}Y`;
}

function safeText(value?: string | null) {
  const text = `${value ?? ''}`.trim();
  return text || '-';
}

function numericFromValue(value: string | number | null | undefined): NumericValue {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const match = value.match(/-?\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : null;
}

function formatNumber(value: NumericValue, fractionDigits?: number) {
  if (value == null || !Number.isFinite(value)) return '-';
  const rounded = Math.round(value * 10) / 10;
  if (fractionDigits != null) return rounded.toFixed(fractionDigits);
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function formatFileNamePart(value?: string | null) {
  return (value || 'UNKNOWN').replace(/[\\/:*?"<>|]+/g, '_').trim() || 'UNKNOWN';
}

export function buildPlaqueAnalysisPdfFileName(patientInfo?: PlaquePdfPatientInfo) {
  const date = normalizeDate(patientInfo?.scanDate).replace(/\./g, '') || 'unknown-date';
  const patient = formatFileNamePart(patientInfo?.patientId || patientInfo?.patientName);
  return `Plaque_Report_${patient}_${date}.pdf`;
}

export function buildPlaqueAnalysisPngFileName(patientInfo?: PlaquePdfPatientInfo, pageIndex = 1) {
  const date = normalizeDate(patientInfo?.scanDate).replace(/\./g, '') || 'unknown-date';
  const patient = formatFileNamePart(patientInfo?.patientId || patientInfo?.patientName);
  return `Plaque_Report_${patient}_${date}_page${pageIndex}.png`;
}

function getMainBranch(data: PlaqueVesselData[]) {
  return data.find((item) => item.vesselName === 'Main branch') ?? data[0];
}

function normalizeVesselName(name: string) {
  const upper = name.toUpperCase().replace(/\s+/g, '');
  if (upper.includes('LAD')) return 'LAD';
  if (upper.includes('LCX')) return 'LCX';
  if (upper.includes('RCA')) return 'RCA';
  if (upper.includes('LM')) return 'LM';
  return name.toUpperCase();
}

function getVesselTableData(mainBranch?: PlaqueVesselData) {
  const result: Partial<Record<Exclude<VesselColumn, 'Total'>, VesselData>> = {};
  if (!mainBranch) return result;

  mainBranch.lesions.forEach((lesion) => {
    const key = normalizeVesselName(lesion.name);
    if (key === 'RCA' || key === 'LAD' || key === 'LCX') {
      result[key] = lesion.tableData.main;
    }
  });
  if (mainBranch.lmTableData) result.LM = mainBranch.lmTableData;
  return result;
}

function buildPlaqueTable(mainBranch?: PlaqueVesselData) {
  const vesselMap = getVesselTableData(mainBranch);
  return plaquePdfRows.map((row) => {
    const values = vesselColumns.map((column) => {
      if (column === 'Total') {
        const total = (['LM', 'LAD', 'LCX', 'RCA'] as const).reduce((sum, key) => {
          const value = numericFromValue(vesselMap[key]?.[row.source]);
          return value == null ? sum : sum + value;
        }, 0);
        const hasValue = (['LM', 'LAD', 'LCX', 'RCA'] as const).some(
          (key) => numericFromValue(vesselMap[key]?.[row.source]) != null,
        );
        return hasValue ? total : null;
      }
      return numericFromValue(vesselMap[column]?.[row.source]);
    });
    return { label: row.label, values };
  });
}

function buildAgatstonTable(agatstonScore?: number[]) {
  const scores = Array.isArray(agatstonScore) ? agatstonScore : [];
  return [
    scores[1] ?? 0,
    scores[2] ?? 0,
    scores[3] ?? 0,
    scores[4] ?? 0,
    scores[0] ?? 0,
  ];
}

async function loadImage(src?: string): Promise<HTMLImageElement | null> {
  if (!src) return null;
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

type DrawImageSource = HTMLImageElement | HTMLCanvasElement | null;

function getSourceSize(img: Exclude<DrawImageSource, null>) {
  if (img instanceof HTMLCanvasElement) {
    return { width: img.width, height: img.height };
  }
  return {
    width: img.naturalWidth || img.width,
    height: img.naturalHeight || img.height,
  };
}

function createCanvasFromImage(img: HTMLImageElement) {
  const { width, height } = getSourceSize(img);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context is not available.');
  ctx.drawImage(img, 0, 0, width, height);
  return canvas;
}

function replaceNearBlackWithWhite(img: HTMLImageElement | null, threshold = 18) {
  if (!img) return null;
  const canvas = createCanvasFromImage(img);
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const { data } = imageData;
  for (let i = 0; i < data.length; i += 4) {
    const alpha = data[i + 3];
    if (!alpha) continue;
    if (data[i] <= threshold && data[i + 1] <= threshold && data[i + 2] <= threshold) {
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = 255;
    }
  }
  ctx.putImageData(imageData, 0, 0);
  return canvas;
}

function trimDarkBorder(img: HTMLImageElement | null, threshold = 18, padding = 4) {
  if (!img) return null;
  const source = createCanvasFromImage(img);
  const ctx = source.getContext('2d');
  if (!ctx) return source;
  const { width, height } = source;
  const { data } = ctx.getImageData(0, 0, width, height);

  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const idx = (y * width + x) * 4;
      const alpha = data[idx + 3];
      if (alpha < 8) continue;
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      if (r > threshold || g > threshold || b > threshold) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (maxX < minX || maxY < minY) return source;

  minX = Math.max(0, minX - padding);
  minY = Math.max(0, minY - padding);
  maxX = Math.min(width - 1, maxX + padding);
  maxY = Math.min(height - 1, maxY + padding);

  const cropped = document.createElement('canvas');
  cropped.width = maxX - minX + 1;
  cropped.height = maxY - minY + 1;
  const croppedCtx = cropped.getContext('2d');
  if (!croppedCtx) return source;
  croppedCtx.drawImage(source, minX, minY, cropped.width, cropped.height, 0, 0, cropped.width, cropped.height);
  return cropped;
}

function drawImageContain(
  ctx: CanvasRenderingContext2D,
  img: DrawImageSource,
  x: number,
  y: number,
  width: number,
  height: number,
  bg = COLOR.white,
) {
  ctx.save();
  ctx.fillStyle = bg;
  ctx.fillRect(x, y, width, height);
  if (img) {
    const sourceSize = getSourceSize(img);
    const scale = Math.min(width / sourceSize.width, height / sourceSize.height);
    const dw = sourceSize.width * scale;
    const dh = sourceSize.height * scale;
    ctx.drawImage(img, x + (width - dw) / 2, y + (height - dh) / 2, dw, dh);
  }
  ctx.restore();
}

function drawRoundedImagePanel(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement | null,
  x: number,
  y: number,
  width: number,
  height: number,
) {
  const processed = replaceNearBlackWithWhite(img);
  ctx.save();
  ctx.fillStyle = COLOR.white;
  ctx.strokeStyle = COLOR.dark;
  ctx.lineWidth = 2;
  ctx.beginPath();
  addRoundRectPath(ctx, x, y, width, height, 8);
  ctx.fill();
  ctx.stroke();
  ctx.clip();
  drawImageContain(ctx, processed, x + 10, y + 10, width - 20, height - 20, COLOR.white);
  ctx.restore();
}

function drawCprPanel(
  ctx: CanvasRenderingContext2D,
  images: Array<{ name: string; image: HTMLImageElement | null }>,
  x: number,
  y: number,
  width: number,
  height: number,
) {
  ctx.save();
  ctx.fillStyle = COLOR.white;
  ctx.strokeStyle = COLOR.dark;
  ctx.lineWidth = 2;
  ctx.beginPath();
  addRoundRectPath(ctx, x, y, width, height, 8);
  ctx.fill();
  ctx.stroke();
  ctx.clip();

  const innerX = x + 18;
  const innerW = width - 36;
  const rowHeight = (height - 34) / 3;
  images.slice(0, 3).forEach((item, index) => {
    const rowY = y + 18 + index * rowHeight;
    const processed = trimDarkBorder(item.image);

    if (index > 0) {
      ctx.strokeStyle = '#d6d6d6';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(innerX, rowY - 2);
      ctx.lineTo(innerX + innerW, rowY - 2);
      ctx.stroke();
    }

    setFont(ctx, 18, 'bold');
    ctx.fillStyle = COLOR.dark;
    ctx.fillText(item.name, innerX + 12, rowY + 20);
    ctx.fillStyle = COLOR.red;
    ctx.fillRect(innerX, rowY + 2, 5, 26);

    const imageBoxX = innerX + 12;
    const imageBoxY = rowY + 34;
    const imageBoxW = innerW - 24;
    const imageBoxH = rowHeight - 48;

    ctx.fillStyle = COLOR.white;
    ctx.fillRect(imageBoxX, imageBoxY, imageBoxW, imageBoxH);
    ctx.strokeStyle = '#cfcfcf';
    ctx.lineWidth = 1;
    ctx.strokeRect(imageBoxX, imageBoxY, imageBoxW, imageBoxH);
    drawImageContain(ctx, processed, imageBoxX + 3, imageBoxY + 3, imageBoxW - 6, imageBoxH - 6, COLOR.white);
  });
  ctx.restore();
}

function drawTable(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  header: string[],
  rows: Array<{ label: string; values: NumericValue[] }>,
  options?: { score?: boolean },
) {
  const itemWidth = options?.score ? 300 : 350;
  const otherWidth = (width - itemWidth) / (header.length - 1);
  const colWidths = [itemWidth, ...Array(header.length - 1).fill(otherWidth)];
  const rowHeight = options?.score ? 47 : 39;
  const headerHeight = 43;
  let currentY = y;

  ctx.fillStyle = COLOR.tableHeader;
  ctx.fillRect(x, currentY, width, headerHeight);
  setFont(ctx, 18, 'bold');
  ctx.fillStyle = COLOR.white;
  let currentX = x;
  header.forEach((text, index) => {
    const colW = colWidths[index];
    ctx.fillText(text, currentX + colW / 2 - ctx.measureText(text).width / 2, currentY + 28);
    ctx.strokeStyle = '#ffffff66';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(currentX, currentY);
    ctx.lineTo(currentX, currentY + headerHeight);
    ctx.stroke();
    currentX += colW;
  });
  currentY += headerHeight;

  rows.forEach((row) => {
    currentX = x;
    ctx.fillStyle = '#fafafa';
    ctx.fillRect(currentX, currentY, colWidths[0], rowHeight);
    setFont(ctx, 18, 'bold');
    ctx.fillStyle = COLOR.dark;
    ctx.fillText(row.label, currentX + 14, currentY + rowHeight / 2 + 7);
    ctx.strokeStyle = COLOR.lightGray;
    ctx.strokeRect(currentX, currentY, colWidths[0], rowHeight);
    currentX += colWidths[0];

    row.values.forEach((value, index) => {
      const isTotal = index === row.values.length - 1;
      const colW = colWidths[index + 1];
      ctx.fillStyle = isTotal ? COLOR.totalBg : COLOR.white;
      ctx.fillRect(currentX, currentY, colW, rowHeight);
      setFont(ctx, 18, isTotal ? 'bold' : 'normal');
      ctx.fillStyle = isTotal ? COLOR.redText : COLOR.dark;
      const text = formatNumber(value, options?.score ? 1 : undefined);
      ctx.fillText(text, currentX + colW / 2 - ctx.measureText(text).width / 2, currentY + rowHeight / 2 + 7);
      ctx.strokeStyle = COLOR.lightGray;
      ctx.strokeRect(currentX, currentY, colW, rowHeight);
      currentX += colW;
    });
    currentY += rowHeight;
  });
  return currentY;
}

function drawInlineSegments(
  ctx: CanvasRenderingContext2D,
  segments: Array<{ text: string; bold?: boolean }>,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  size: number,
) {
  let currentX = x;
  let currentY = y;
  segments.forEach((segment) => {
    const parts = segment.text.split(/(\s+)/);
    parts.forEach((part) => {
      if (part === '') return;
      setFont(ctx, size, segment.bold ? 'bold' : 'normal');
      ctx.fillStyle = COLOR.dark;
      const width = ctx.measureText(part).width;
      if (currentX + width > x + maxWidth && !/^\s+$/.test(part)) {
        currentX = x;
        currentY += lineHeight;
      }
      ctx.fillText(part, currentX, currentY);
      currentX += width;
    });
  });
  return currentY + lineHeight;
}

async function renderPage1(input: PlaquePdfExportInput) {
  const { canvas, ctx } = createPageCanvas();
  const patient = input.patientInfo ?? {};
  const marginX = 90;

  const logo = await loadImage(aimedicLogoUrl);
  drawAimedicLogo(ctx, logo, 78, 50, 270, 62);
  setFont(ctx, 58, 'bold');
  ctx.fillStyle = COLOR.dark;
  ctx.fillText('Plaque', marginX, 230);
  setFont(ctx, 48, 'bold');
  ctx.fillText('Coronary Plaque Analysis Report', marginX, 307);

  setFont(ctx, 20, 'normal');
  ctx.fillStyle = '#777777';
  ctx.fillText(`Scan Date: ${normalizeDate(patient.scanDate)}`, marginX, 376);
  const patientIdText = `Patient ID: ${safeText(patient.patientId)}`;
  ctx.fillText(patientIdText, 930, 376);

  setFont(ctx, 30, 'bold');
  ctx.fillStyle = COLOR.dark;
  const patientLine = `${safeText(patient.patientName)}  (${normalizeSex(patient.patientSex)} / ${normalizeDate(patient.patientBirthdate)} / ${normalizeAge(patient.patientAge)})`;
  const patientLineWidth = ctx.measureText(patientLine).width;
  ctx.fillText(patientLine, Math.max(marginX, PAGE_WIDTH - marginX - patientLineWidth), 418);

  drawSectionHeader(ctx, 'What is Coronary Plaque (Atheroma)?', marginX, 564, PAGE_WIDTH - marginX * 2);
  drawMultiline(ctx, [
    'Plaque refers to deposits of cholesterol, fat, and calcium that accumulate on the arterial walls.',
    'As plaque builds up, the vessels may narrow or stiffen, impairing blood flow.',
    'The amount of plaque is an important reference indicator in the management of cardiovascular disease.',
  ], marginX + 52, 655, PAGE_WIDTH - marginX * 2 - 52, 39, 24);

  drawSectionHeader(ctx, 'Types of Plaque', marginX, 976, PAGE_WIDTH - marginX * 2);
  drawMultiline(ctx, [
    'Calcified Plaque: Hardened plaque formed by the deposition of calcium.',
    'Noncalcified Plaque: Plaque composed of soft-tissue components such as lipid.',
    'Low Attenuation Plaque: Plaque with a high proportion of lipid content.',
  ], marginX + 52, 1082, PAGE_WIDTH - marginX * 2 - 52, 40, 24);

  drawSectionHeader(ctx, 'What is Plaque Analysis?', marginX, 1378, PAGE_WIDTH - marginX * 2);
  drawMultiline(ctx, [
    'An advanced examination that quantitatively analyzes the amount and composition of plaque in the coronary arteries based on cardiac CT imaging.',
    'It enables assessment of plaque distribution within the vessels without invasive procedures, reducing patient burden,',
    'and may help with the early detection of cardiovascular disease and preventive health management.',
  ], marginX + 52, 1450, PAGE_WIDTH - marginX * 2 - 52, 38, 22);

  setFont(ctx, 17, 'italic');
  ctx.fillStyle = '#777777';
  ctx.fillText('※ Computational analysis result, intended as a reference to support clinical judgment.', marginX, 1682);

  return canvas;
}

async function renderPage2(input: PlaquePdfExportInput) {
  const { canvas, ctx } = createPageCanvas();
  const mainBranch = getMainBranch(input.reportData);
  const plaqueRows = buildPlaqueTable(mainBranch);
  const agatstonValues = buildAgatstonTable(input.agatstonScore);
  const plaqueVolumeTotal = plaqueRows.find((row) => row.label.startsWith('Plaque Volume'))?.values[4] ?? null;
  const agatstonTotal = agatstonValues[4] ?? 0;
  const marginX = 74;
  const contentW = PAGE_WIDTH - marginX * 2;

  drawSectionHeader(ctx, 'Coronary Plaque Analysis Results', marginX, 146, contentW);
  drawMultiline(ctx, [
    'Plaque analysis results from multi-view evaluation of the coronary arteries.',
    'The 3D model shows vessel course; CPR images show unfolded cross-sections.',
  ], marginX + 16, 210, contentW - 32, 36, 23, COLOR.dark);

  const threeD = await loadImage(input.threeDImage);
  const cprSources = ['RCA', 'LAD', 'LCX'].map((name) => {
    const lesion = mainBranch?.lesions.find((item) => normalizeVesselName(item.name) === name);
    return {
      name: name === 'LAD' || name === 'LCX' ? `${name} + LM` : name,
      src: lesion?.images?.[0],
    };
  });
  const cprImages = await Promise.all(cprSources.map(async (item) => ({
    name: item.name,
    image: await loadImage(item.src),
  })));

  const imageTop = 286;
  const imageHeight = 438;
  drawRoundedImagePanel(ctx, threeD, marginX, imageTop, 410, imageHeight);
  drawCprPanel(ctx, cprImages, marginX + 432, imageTop, contentW - 432, imageHeight);

  setFont(ctx, 20, 'normal');
  ctx.fillStyle = '#888888';
  ctx.fillText('Coronary 3D Model', marginX + 122, imageTop + imageHeight + 31);
  ctx.fillText('CPR Images per Vessel (RCA / LAD+LM / LCX+LM)', marginX + 580, imageTop + imageHeight + 31);

  setFont(ctx, 31, 'bold');
  ctx.fillStyle = COLOR.dark;
  ctx.fillText('Quantitative Plaque Analysis per Vessel', marginX + 16, 825);

  drawTable(
    ctx,
    marginX,
    846,
    contentW,
    ['Item', ...vesselColumns],
    plaqueRows,
  );

  setFont(ctx, 31, 'bold');
  ctx.fillStyle = COLOR.dark;
  ctx.fillText('Coronary Calcium Score (Agatston Score)', marginX + 16, 1192);

  drawTable(
    ctx,
    marginX,
    1214,
    contentW,
    ['Vessel', ...vesselColumns],
    [{ label: 'Agatston Score', values: agatstonValues }],
    { score: true },
  );

  const hasCalcifiedPlaque = Number.isFinite(agatstonTotal) && agatstonTotal > 0;
  const agatstonResultText = hasCalcifiedPlaque ? formatNumber(agatstonTotal, 1) : '0';

  drawInlineSegments(ctx, [
    { text: 'Analysis revealed a total ' },
    { text: `Plaque Volume of ${formatNumber(plaqueVolumeTotal)} mm\u00b3`, bold: true },
    { text: ', with an ' },
    { text: `Agatston Score of ${agatstonResultText}`, bold: true },
    { text: `, indicating the ${hasCalcifiedPlaque ? 'presence' : 'absence'} of calcified plaque.` },
  ], marginX + 16, 1376, contentW - 32, 32, 23);

  setFont(ctx, 17, 'italic');
  ctx.fillStyle = '#777777';
  const notes = [
    '※ Computational analysis result, intended as a reference to support clinical judgment.',
    '※ Plaque analysis values can be influenced by multiple factors, and diagnosis should not be based on this result alone.',
    '※ Accurate interpretation and future management plans should be determined through sufficient consultation with the attending clinician.',
  ];
  let noteY = 1468;
  notes.forEach((note) => {
    noteY = drawWrappedText(ctx, note, marginX + 16, noteY, contentW - 32, 25, 17, '#777777', 'italic');
  });

  return canvas;
}

function dataUrlToBytes(dataUrl: string) {
  const base64 = dataUrl.split(',')[1] ?? '';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function canvasToPdfImagePage(canvas: HTMLCanvasElement): PdfImagePage {
  const url = canvas.toDataURL('image/jpeg', 0.93);
  return {
    data: dataUrlToBytes(url),
    width: canvas.width,
    height: canvas.height,
  };
}

function concatBytes(parts: Uint8Array[], totalLength: number) {
  const out = new Uint8Array(totalLength);
  let offset = 0;
  parts.forEach((part) => {
    out.set(part, offset);
    offset += part.length;
  });
  return out;
}

function buildPdfFromImagePages(pages: PdfImagePage[]) {
  const encoder = new TextEncoder();
  const parts: Uint8Array[] = [];
  const offsets: number[] = [0];
  let byteLength = 0;

  const push = (chunk: Uint8Array | string) => {
    const bytes = typeof chunk === 'string' ? encoder.encode(chunk) : chunk;
    parts.push(bytes);
    byteLength += bytes.length;
  };

  const addObject = (id: number, chunks: Array<Uint8Array | string>) => {
    offsets[id] = byteLength;
    push(`${id} 0 obj\n`);
    chunks.forEach(push);
    push('\nendobj\n');
  };

  push('%PDF-1.4\n%\u00e2\u00e3\u00cf\u00d3\n');

  const pageObjectIds = pages.map((_, index) => 3 + index * 3);
  const imageObjectIds = pages.map((_, index) => 4 + index * 3);
  const contentObjectIds = pages.map((_, index) => 5 + index * 3);
  const maxObjectId = 2 + pages.length * 3;

  addObject(1, ['<< /Type /Catalog /Pages 2 0 R >>']);
  addObject(2, [`<< /Type /Pages /Kids [${pageObjectIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pages.length} >>`]);

  pages.forEach((page, index) => {
    const pageId = pageObjectIds[index];
    const imageId = imageObjectIds[index];
    const contentId = contentObjectIds[index];
    const imageName = `Im${index + 1}`;
    const content = `q\n${PDF_PAGE_WIDTH} 0 0 ${PDF_PAGE_HEIGHT} 0 0 cm\n/${imageName} Do\nQ`;
    const contentBytes = encoder.encode(content);

    addObject(pageId, [
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PDF_PAGE_WIDTH} ${PDF_PAGE_HEIGHT}] /Resources << /XObject << /${imageName} ${imageId} 0 R >> >> /Contents ${contentId} 0 R >>`,
    ]);
    addObject(imageId, [
      `<< /Type /XObject /Subtype /Image /Width ${page.width} /Height ${page.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${page.data.length} >>\nstream\n`,
      page.data,
      '\nendstream',
    ]);
    addObject(contentId, [
      `<< /Length ${contentBytes.length} >>\nstream\n`,
      contentBytes,
      '\nendstream',
    ]);
  });

  const xrefOffset = byteLength;
  push(`xref\n0 ${maxObjectId + 1}\n`);
  push('0000000000 65535 f \n');
  for (let id = 1; id <= maxObjectId; id += 1) {
    push(`${String(offsets[id] ?? 0).padStart(10, '0')} 00000 n \n`);
  }
  push(`trailer\n<< /Size ${maxObjectId + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`);

  return concatBytes(parts, byteLength);
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
        return;
      }
      reject(new Error('Failed to encode image blob.'));
    }, type, quality);
  });
}

async function renderPlaqueAnalysisPages(input: PlaquePdfExportInput) {
  const page1 = await renderPage1(input);
  const page2 = await renderPage2(input);
  return [page1, page2] as const;
}


export async function createPlaqueAnalysisPageImageUrls(input: PlaquePdfExportInput): Promise<string[]> {
  if (!input.reportData?.length) {
    throw new Error('Plaque report data is not available.');
  }

  const pages = await renderPlaqueAnalysisPages(input);
  return pages.map((page) => page.toDataURL('image/png'));
}

export async function createPlaqueAnalysisPngBlobs(input: PlaquePdfExportInput): Promise<Blob[]> {
  if (!input.reportData?.length) {
    throw new Error('Plaque report data is not available.');
  }

  const pages = await renderPlaqueAnalysisPages(input);
  return Promise.all(pages.map((page) => canvasToBlob(page, 'image/png')));
}

export async function createPlaqueAnalysisPdfBlob(input: PlaquePdfExportInput): Promise<Blob> {
  if (!input.reportData?.length) {
    throw new Error('Plaque report data is not available.');
  }

  const pages = await renderPlaqueAnalysisPages(input);
  const pdfBytes = buildPdfFromImagePages(pages.map((page) => canvasToPdfImagePage(page)));

  return new Blob([pdfBytes], { type: 'application/pdf' });
}
